<template>
    <div
        ref="header"
        class="gst-column-header d-flex align-center u-width-100">
        <div class="flex-grow-1 d-flex align-center">
            <div
                class="gst-column-header__label ml-4 u-mouse-pointer"
                @click="toggleSort">
                <div v-if="!params.i18nKeys">
                    {{ params.displayName }}
                </div>
                <div v-else>
                    <!-- eslint-disable-next-line vue/no-v-html -->
                    <div
                        v-for="(i18nKey, key) in params.i18nKeys"
                        :key="key"
                        :class="{ 'font-weight-light': key !== 0 }"
                        v-html="$t(i18nKey)"></div>
                </div>
            </div>
            <v-icon
                v-if="sortIcon"
                :icon="sortIcon" />
        </div>
        <v-menu location="bottom">
            <template v-slot:activator="{ props }">
                <BaseButton
                    name="columnHeaderMenu"
                    size="x-small"
                    color="transparent"
                    elevation="0"
                    v-bind="props">
                    <v-icon>mdi-dots-vertical</v-icon>
                </BaseButton>
            </template>

            <v-list density="compact">
                <v-list-item
                    v-if="!isSortedAsc"
                    @click="setSort('asc')">
                    <v-icon icon="mdi-arrow-up" />
                    {{ $t('sortAsc') }}
                </v-list-item>
                <v-list-item
                    v-if="!isSortedDesc"
                    @click="setSort('desc')">
                    <v-icon icon="mdi-arrow-down" />
                    {{ $t('sortDesc') }}
                </v-list-item>
                <v-list-item
                    v-if="isSortedAsc || isSortedDesc"
                    @click="setSort('null')">
                    <v-icon icon="mdi-unfold-more-horizontal" />
                    {{ $t('clearSort') }}
                </v-list-item>
                <v-divider />
                <v-list-item>
                    <v-icon icon="mdi-pin-outline" />
                    {{ $t('pinColumn') }}
                    <template v-slot:append>
                        <v-icon
                            icon="mdi-menu-right"
                            size="x-small" />
                    </template>
                    <v-menu
                        :open-on-focus="false"
                        activator="parent"
                        :open-on-hover="true"
                        submenu
                        location="right">
                        <v-list>
                            <v-list-item @click="setPinned(null)">
                                <v-icon
                                    v-show="!isPinned"
                                    icon="mdi-check" />
                                {{ $t('noPin') }}
                            </v-list-item>
                            <v-list-item @click="setPinned('left')">
                                <v-icon
                                    v-show="isPinnedLeft"
                                    icon="mdi-check" />
                                {{ $t('pinLeft') }}
                            </v-list-item>
                            <v-list-item @click="setPinned('right')">
                                <v-icon
                                    v-show="isPinnedRight"
                                    icon="mdi-check" />
                                {{ $t('pinRight') }}
                            </v-list-item>
                        </v-list>
                    </v-menu>
                </v-list-item>
                <v-divider />
                <v-list-item
                    ref="chooseColumn"
                    @click="openChooseColumns">
                    <v-icon icon="mdi-table-cog" />
                    {{ $t('chooseColumns') }}
                </v-list-item>
                <v-list-item @click="resetColumns">
                    {{ $t('resetColumns') }}
                </v-list-item>
            </v-list>
        </v-menu>

        <v-menu
            v-model="chooseColumnsOpen"
            :attach="getGridElement()"
            :close-on-content-click="false"
            density="compact">
            <v-list>
                <div class="d-flex align-center u-width-100 px-4">
                    <span class="flex-grow-1">{{ $t('chooseColumns') }}</span>
                    <v-btn
                        icon="mdi-close"
                        size="x-small"
                        color="transparent"
                        elevation="0"
                        @click="chooseColumnsOpen = false" />
                </div>
                <v-list-item
                    v-for="column in columnsWithVisibility"
                    :key="column.field"
                    class="py-0"
                    density="compact">
                    <v-list-item-action>
                        <v-checkbox
                            v-model="column.visible"
                            density="compact"
                            hide-details
                            @click.stop
                            @update:model-value="toggleColumnVisibility(column)">
                            <template v-slot:label>
                                {{ column.headerName || column.field }}
                            </template>
                        </v-checkbox>
                    </v-list-item-action>
                </v-list-item>
            </v-list>
        </v-menu>
    </div>
</template>

<script>
import BaseButton from '@components/buttons/BaseButton';

export default {
    name: 'BaseGridColumnHeader',
    components: {
        BaseButton,
    },
    i18nOptions: {
        namespaces: '_common',
        keyPrefix: 'grid.columnHeader',
    },
    props: {
        params: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            chooseColumnsOpen: false,
            sort: null,
            pin: null,
            columnsWithVisibility: [],
        };
    },
    computed: {
        sortIcon() {
            if (this.isSortedAsc) {
                return 'mdi-arrow-up';
            }
            if (this.isSortedDesc) {
                return 'mdi-arrow-down';
            }
            return '';
        },
        isSortedAsc() {
            return this.sort === 'asc';
        },
        isSortedDesc() {
            return this.sort === 'desc';
        },
        isPinnedLeft() {
            return this.pin === 'left';
        },
        isPinnedRight() {
            return this.pin === 'right';
        },
        isPinned() {
            return this.isPinnedRight || this.isPinnedLeft;
        },
    },
    methods: {
        openChooseColumns() {
            this.chooseColumnsOpen = !this.chooseColumnsOpen;
            if (this.chooseColumnsOpen) {
                this.updateColumnsWithVisibility();
            }
        },
        toggleSort(event) {
            this.params.progressSort(event.shiftKey);
        },
        onSortChanged() {
            this.sort = this.params.column.getSort();
        },
        setSort(direction) {
            this.params.api.applyColumnState({
                state: [{ colId: this.params.column.getId(), sort: direction }],
                applyOrder: false,
            });
        },
        setPinned(position) {
            this.params.api.setColumnPinned(this.params.column, position);
            this.updatePinnedState();
        },
        updatePinnedState() {
            this.pin = this.params.column.getPinned();
        },
        toggleColumnVisibility(column) {
            this.params.api.setColumnVisible(column.field, column.visible);
            this.updateColumnsWithVisibility();
        },
        updateColumnsWithVisibility() {
            const api = this.params.api;
            const columnState = api.getColumnState();
            const columnDefs = api.columnModel.columnDefs;
            this.columnsWithVisibility = columnDefs.map((column) => ({
                ...column,
                visible: !columnState.find((col) => col.colId === column.field)?.hide,
            }));
        },
        resetColumns() {
            this.params.api.resetColumnState();
            this.params.api.sizeColumnsToFit();
            this.updateColumnsWithVisibility();
        },
        getGridElement() {
            return this.params.eGridHeader.closest('.gst-base-grid');
        },
    },
    mounted() {
        this.updateColumnsWithVisibility();
        this.updatePinnedState();
        this.params.column.addEventListener('sortChanged', this.onSortChanged);
        this.onSortChanged();
    },
    unmounted() {
        this.params.column.removeEventListener('sortChanged', this.onSortChanged);
    },
};
</script>
